import type { ItemMarketplaces, MarketFigures, MarketplaceRow } from '../api';

/** True when any marketplace has said anything about this colour and condition. */
export const hasFigures = (r: MarketplaceRow): boolean =>
  [r.bricklink.sold, r.bricklink.forSale, r.brickowl.forSale, r.ebay.forSale].some((f) => f !== null);

/**
 * Which rows to show. An item can have dozens of colour children, so by default only the colours that matter are
 * listed: those with stock or pieces on order, and those a marketplace has figures for. Everything else is one click away.
 */
export function visibleRows(data: ItemMarketplaces, inUse: Set<number>, showAll: boolean): { rows: MarketplaceRow[]; hidden: number } {
  if (showAll) return { rows: data.rows, hidden: 0 };
  const rows = data.rows.filter((r) => inUse.has(r.colorId) || hasFigures(r));
  return { rows, hidden: data.rows.length - rows.length };
}

/** True when some figure has no currency, so the page can say the marketplace did not state one. */
export const anyCurrencyUnstated = (rows: MarketplaceRow[], pick: (r: MarketplaceRow) => MarketFigures | null): boolean =>
  rows.some((r) => {
    const f = pick(r);
    return f !== null && f.status === 'ok' && f.currency === null && (f.minPrice !== null || f.avgPrice !== null);
  });

/** How much older than a day a figure is, for the "out of date" wording. */
export const ageDays = (fetchedAt: string, now = Date.now()): number => Math.floor((now - new Date(fetchedAt).getTime()) / 86_400_000);
